import React from 'react';
import { IndianRupee, TrendingUp, AlertTriangle, Server } from 'lucide-react';
import {
  AreaChart, Area, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid,
} from 'recharts';
import {
  ChartContainer, ChartTooltip, ChartTooltipContent, ChartLegend, ChartLegendContent,
} from '@/components/ui/chart';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
} from '@/components/ui/table';
import {
  KpiCard, ChartCard, ExportMenu, ScreenHeader, DonutCenter, EmptyMeter, UnconvertibleNote, UnpricedBadge,
  AXIS_TICK, CHART_FILL,
} from '@/components/app/kit.jsx';
import { useOps } from '@/hooks/useOps.jsx';
import { fetchRollups, enrich } from '@/lib/ops-data.js';
import { totalINR, groupSum, ranked, dailyStacked } from '@/lib/aggregate.js';
import { fmtINR, fmtCompact, categoryOfProvider, shiftDay } from '@/lib/format.js';
import { downloadCsv, downloadTxtReport } from '@/lib/export.js';

const CATEGORIES = ['LLM', 'STT', 'STT/TTS', 'Voice/TTS', 'Voice/LLM', 'Voice', 'Messaging', 'AWS', 'Payments', 'Store', 'Misc'];

export default function Overview({ range }) {
  const ops = useOps();
  const [prevTotal, setPrevTotal] = React.useState(null);

  // Delta vs the immediately-preceding equal-length window (a real second fetch —
  // no synthetic baseline). Uses the already-loaded prices/fx for parity.
  React.useEffect(() => {
    if (!ops.byKey) return undefined;
    let alive = true;
    const days = Math.round((Date.parse(range.to) - Date.parse(range.from)) / 86400000) + 1;
    const prevTo = shiftDay(range.from, -1);
    const prevFrom = shiftDay(prevTo, -(days - 1));
    fetchRollups({ from: prevFrom, to: prevTo })
      .then((rows) => { if (alive) setPrevTotal(totalINR(enrich(rows, ops.byKey, ops.fx)).inr); })
      .catch(() => { if (alive) setPrevTotal(null); });
    return () => { alive = false; };
  }, [range.from, range.to, ops.byKey, ops.fx]);

  if (ops.loading) return <LoadingBody />;
  if (ops.error) return <ErrorBody error={ops.error} />;

  const items = ops.enriched;
  if (!items.length) return <div className="p-6"><ScreenHeaderStub /><EmptyMeter /></div>;

  const grand = totalINR(items);
  const byProvider = ranked(groupSum(items, (r) => r.provider));
  const topDriver = byProvider[0];
  const byCategory = ranked(groupSum(items, (r) => categoryOfProvider(r.provider)));

  const catSegs = byCategory.filter((c) => c.inr > 0).map((c, i) => ({
    key: c.key, label: c.key, value: Math.round(c.inr), fill: CHART_FILL(i),
  }));

  const activeCats = CATEGORIES.filter((c) => byCategory.some((r) => r.key === c && r.inr > 0));
  const stacked = dailyStacked(items, (r) => categoryOfProvider(r.provider), activeCats, range);
  const chartConfig = Object.fromEntries(activeCats.map((c, i) => [c, { label: c, color: CHART_FILL(i) }]));

  const top5 = byProvider.slice(0, 5);

  // ── Exports ──
  const csv = () => downloadCsv(
    `ops-overview-${range.from}_${range.to}.csv`,
    [
      { key: 'provider', label: 'Provider' },
      { key: 'category', label: 'Category' },
      { key: 'inr', label: 'Spend (INR)' },
      { key: 'share', label: 'Share %' },
      { key: 'unpriced', label: 'Unpriced records' },
    ],
    byProvider.map((r) => ({
      provider: r.key, category: categoryOfProvider(r.key),
      inr: Math.round(r.inr), share: r.share.toFixed(1), unpriced: r.unpricedCount,
    })),
  );
  const txt = () => downloadTxtReport(`ops-overview-${range.from}_${range.to}.txt`, {
    title: 'Healther Ops — Cost Overview',
    meta: [
      `Range: ${range.from} → ${range.to}`,
      `Total spend (INR): ${fmtINR(grand.inr)}`,
      `Top cost driver: ${topDriver ? `${topDriver.key} (${fmtINR(topDriver.inr)})` : '—'}`,
      `Unpriced records: ${grand.unpricedCount}`,
    ],
    sections: [{
      heading: 'Spend by provider',
      columns: [{ key: 'provider', label: 'Provider' }, { key: 'inr', label: 'Spend (INR)' }, { key: 'share', label: 'Share %' }],
      rows: byProvider.map((r) => ({ provider: r.key, inr: Math.round(r.inr), share: r.share.toFixed(1) + '%' })),
    }],
  });

  return (
    <div className="p-6 cc-enter">
      <ScreenHeader title="Overview" subtitle={`Platform spend and cost drivers · ${range.from} → ${range.to}`}>
        <ExportMenu onCsv={csv} onTxt={txt} />
      </ScreenHeader>

      <UnconvertibleNote currencies={ops.unconvertible} />

      <div className="mb-4 flex flex-wrap gap-3">
        <KpiCard label="Total spend" icon={IndianRupee} value={fmtINR(grand.inr)}
          now={grand.inr} prev={prevTotal ?? undefined} goodWhenDown
          sub="range total, INR" />
        <KpiCard label="Top cost driver" icon={TrendingUp}
          value={topDriver ? topDriver.key : '—'}
          sub={topDriver ? `${fmtINR(topDriver.inr)} · ${topDriver.share.toFixed(0)}%` : ''} />
        <KpiCard label="Categories" icon={Server} value={catSegs.length}
          sub={catSegs.slice(0, 3).map((c) => c.label).join(', ')} />
        <KpiCard label="Unpriced records" icon={AlertTriangle}
          tone={grand.unpricedCount ? 'text-[hsl(var(--ai-foreground))]' : 'text-foreground'}
          value={grand.unpricedCount}
          sub={grand.unpricedCount ? `${fmtCompact(grand.unpricedQty)} qty un-costed` : 'all usage priced'} />
      </div>

      <div className="grid grid-cols-1 gap-3 lg:grid-cols-3">
        <ChartCard title="Spend by category" subtitle="Share of INR spend" className="lg:col-span-1"
          empty={catSegs.length === 0}>
          <div className="relative">
            <ChartContainer config={Object.fromEntries(catSegs.map((s) => [s.key, { label: s.label, color: s.fill }]))}
              className="mx-auto h-[210px] w-full">
              <PieChart>
                <ChartTooltip cursor={false} content={<ChartTooltipContent nameKey="label" formatter={(v) => fmtINR(v)} hideLabel />} />
                <Pie data={catSegs} dataKey="value" nameKey="label" innerRadius={56} outerRadius={84} strokeWidth={2} paddingAngle={2}>
                  {catSegs.map((s) => <Cell key={s.key} fill={s.fill} />)}
                </Pie>
                <ChartLegend content={<ChartLegendContent nameKey="label" />} />
              </PieChart>
            </ChartContainer>
            <div className="pointer-events-none absolute inset-x-0 top-0 flex h-[210px] items-center justify-center pb-10">
              <DonutCenter primary={fmtINR(grand.inr)} secondary="total" />
            </div>
          </div>
        </ChartCard>

        <ChartCard title="Daily spend" subtitle="Stacked by category (INR)" className="lg:col-span-2"
          headline={fmtINR(grand.inr)} delta={{ now: grand.inr, prev: prevTotal ?? undefined, goodWhenDown: true }}
          empty={stacked.length === 0}>
          <ChartContainer config={chartConfig} className="h-[210px] w-full">
            <AreaChart data={stacked} margin={{ top: 6, right: 6, left: 0, bottom: 0 }}>
              <defs>
                {activeCats.map((c, i) => (
                  <linearGradient key={c} id={`fill-${i}`} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={CHART_FILL(i)} stopOpacity={0.35} />
                    <stop offset="95%" stopColor={CHART_FILL(i)} stopOpacity={0.04} />
                  </linearGradient>
                ))}
              </defs>
              <CartesianGrid vertical={false} strokeDasharray="3 3" />
              <XAxis dataKey="label" tickLine={false} axisLine={false} tickMargin={8} tick={AXIS_TICK} minTickGap={24} />
              <YAxis width={52} tickLine={false} axisLine={false} tick={AXIS_TICK} tickFormatter={(v) => fmtCompact(v)} />
              <ChartTooltip content={<ChartTooltipContent formatter={(v) => fmtINR(v)} />} />
              <ChartLegend content={<ChartLegendContent />} />
              {activeCats.map((c, i) => (
                <Area key={c} dataKey={c} type="monotone" stackId="cat"
                  stroke={CHART_FILL(i)} strokeWidth={1.5} fill={`url(#fill-${i})`} />
              ))}
            </AreaChart>
          </ChartContainer>
        </ChartCard>
      </div>

      <Card className="mt-4 gap-0 overflow-hidden py-0">
        <CardHeader className="border-b py-4">
          <CardTitle className="text-[13.5px]">Top providers</CardTitle>
          <CardDescription className="text-[11.5px]">Highest spend in this range</CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="pl-4">Provider</TableHead>
                <TableHead>Category</TableHead>
                <TableHead className="text-right">Spend (INR)</TableHead>
                <TableHead className="text-right">Share</TableHead>
                <TableHead className="pr-4 text-right">Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {top5.map((r) => (
                <TableRow key={r.key}>
                  <TableCell className="pl-4 font-medium text-foreground">{r.key}</TableCell>
                  <TableCell className="text-muted-foreground">{categoryOfProvider(r.key)}</TableCell>
                  <TableCell className="text-right tabular-nums font-medium text-foreground">{fmtINR(r.inr)}</TableCell>
                  <TableCell className="text-right tabular-nums text-muted-foreground">{r.share.toFixed(1)}%</TableCell>
                  <TableCell className="pr-4 text-right">
                    {r.unpricedCount ? <UnpricedBadge qty={r.unpricedQty} count={r.unpricedCount} /> : <span className="text-[11px] text-muted-foreground">priced</span>}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}

function ScreenHeaderStub() {
  return <ScreenHeader title="Overview" subtitle="Platform spend and cost drivers" />;
}
export function LoadingBody() {
  return (
    <div className="p-6">
      <div className="mb-4 flex gap-3">{[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-24 flex-1 rounded-xl" />)}</div>
      <div className="grid grid-cols-3 gap-3">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-56 rounded-xl" />)}</div>
      <Skeleton className="mt-4 h-48 rounded-xl" />
    </div>
  );
}
export function ErrorBody({ error }) {
  return (
    <div className="p-6">
      <div className="flex items-start gap-2 rounded-lg border border-destructive/40 bg-destructive/10 px-4 py-3 text-[13px] font-medium text-destructive">
        <AlertTriangle className="mt-0.5 size-4 flex-none" />
        <div><div className="font-semibold">Could not load ops data.</div><div className="mt-0.5 opacity-90">{error}</div></div>
      </div>
    </div>
  );
}
